import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Loader2, ShieldQuestion, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import useWin98Scope from '../hooks/useWin98Scope';

// Landing page for the "Export to KyberCosplay" hand-off from MandoMaster.
// MandoMaster redirects here with ?token=<single-use>&src=<origin>. We confirm
// the user is signed in (bouncing through /login and back if not), then let
// them pull the build in. The export token is single-use, so the actual fetch
// happens exactly once — server-side, inside the import endpoint.
const ImportMandomaster = () => {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  useWin98Scope();

  const token = searchParams.get('token');
  const src = searchParams.get('src');
  const share = searchParams.get('share') === '1';

  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  // Send unauthenticated users to login, preserving the return path so they
  // land back here (with the token intact) once signed in.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const returnTo = window.location.pathname + window.location.search;
      localStorage.setItem('postLoginRedirect', returnTo);
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleImport = async () => {
    if (!token || !src) {
      setError('This import link is missing its token. Please re-export from MandoMaster.');
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const res = await api.post('/cosplays/import-mandomaster', {
        token,
        source_url: src,
        share_to_community: share,
      });
      toast.success(share ? 'Build shared to the community!' : 'Build imported!');
      navigate(`/cosplays/${res.data.cosplay_id}`, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 410) {
        setError('This export link has expired or was already used. Re-export from MandoMaster to try again.');
      } else {
        setError(detail || 'Import failed. Please try again.');
      }
      setImporting(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  const missingToken = !token || !src;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="win-window">
          <div className="win-titlebar">
            <span>Import from MandoMaster</span>
          </div>
          <div className="p-6 text-center">
            {missingToken ? (
              <div className="flex flex-col items-center gap-4">
                <ShieldQuestion className="w-10 h-10 text-[var(--win-titlebar-start)]" />
                <p className="text-black">
                  This import link is incomplete. Head back to MandoMaster and use
                  <span className="text-[var(--win-titlebar-start)] font-semibold"> Export to KyberCosplay</span> again.
                </p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="win-btn-default"
                >
                  Go to Dashboard
                </Button>
              </div>
            ) : (
              <>
                <p className="text-black mb-6">
                  We'll create a new cosplay project from your armor build — with a
                  reference image, a task for every piece, and your soft-goods
                  checklist ready to go.
                </p>

                {error && (
                  <div className="win-well px-4 py-3 mb-5 text-sm text-left flex items-start gap-2">
                    <span aria-hidden="true" className="text-[hsl(var(--destructive))]">⚠</span>
                    <span className="text-black">{error}</span>
                  </div>
                )}

                <Button
                  onClick={handleImport}
                  disabled={importing}
                  data-testid="import-mandomaster-btn"
                  className="w-full h-12 win-btn-accent win-btn-default font-semibold"
                >
                  {importing ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" /> Importing…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Import Build <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                <button
                  onClick={() => navigate('/dashboard')}
                  disabled={importing}
                  className="mt-4 text-sm win-btn-default disabled:opacity-40"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportMandomaster;
